import { useState, useCallback, useEffect, useRef } from 'react';
import { FaArrowCircleUp, FaArrowCircleDown, FaArrowCircleLeft, FaArrowCircleRight } from "react-icons/fa";

const DIRECTIONS = ['N', 'E', 'S', 'W'];
const VECTORS = { N: [0, -1], E: [1, 0], S: [0, 1], W: [-1, 0] };
const DIRECTION_NAMES = { N: 'North', E: 'East', S: 'South', W: 'West' };

const MazeWalker = ({ grid, playerPos, setPlayerPos, width = 400, height = 300 }) => {
  const [facing, setFacing] = useState('E');

  const canvasRef = useRef(null);
    const [player, setPlayer] = useState({ x: 0.5, y: 0.5, dir: 'E' });
  
    const cols = grid[0].length;
    const rows = grid.length;
    
    // Movement logic
    const move = useCallback((forward = true) => {
      const [dx, dy] = VECTORS[player.dir];
      const newX = player.x + (forward ? dx : -dx);
      const newY = player.y + (forward ? dy : -dy);
      const cx = Math.floor(player.x);
      const cy = Math.floor(player.y);
      const dirIdx = DIRECTIONS.indexOf(player.dir);
  
      if (
        newX >= 0 && newY >= 0 &&
        newX < cols && newY < rows &&
        !grid[cy][cx].walls[forward ? dirIdx : (dirIdx + 2) % 4]
      ) {
        setPlayer(p => ({ ...p, x: newX, y: newY }));
      }
    }, [player, grid, cols, rows]);
  
    const rotate = (left = true) => {
      setPlayer(p => {
        const dirIndex = DIRECTIONS.indexOf(p.dir);
        const newDir = DIRECTIONS[(dirIndex + (left ? 3 : 1)) % 4];
        return { ...p, dir: newDir };
      });
    };
  
    // Drawing logic
    useEffect(() => {
      const ctx = canvasRef.current.getContext('2d');
      ctx.clearRect(0, 0, width, height);
  
      const fov = Math.PI / 3;
      const numRays = width;
      const stepSize = 0.01;
  
      const baseAngle = {
        N: -Math.PI / 2,
        E: 0,
        S: Math.PI / 2,
        W: Math.PI
      }[player.dir];
  
      for (let i = 0; i < numRays; i++) {
        const rayAngle = baseAngle - fov / 2 + (i / numRays) * fov;
        let rayX = player.x;
        let rayY = player.y;
        let dist = 0;
        let hit = false;
  
        while (!hit && dist < 20) {
          const prevX = Math.floor(rayX);
          const prevY = Math.floor(rayY);
  
          rayX += Math.cos(rayAngle) * stepSize;
          rayY += Math.sin(rayAngle) * stepSize;
          dist += stepSize;
  
          const curX = Math.floor(rayX);
          const curY = Math.floor(rayY);
  
          if (curX < 0 || curY < 0 || curX >= cols || curY >= rows) {
            hit = true;
          } else if (curX !== prevX || curY !== prevY) {
            const dx = curX - prevX;
            const dy = curY - prevY;
            const wallIdx =
              dx === 1 ? 1 :
              dx === -1 ? 3 :
              dy === 1 ? 2 :
              dy === -1 ? 0 : -1;
            const prevCell = grid[prevY][prevX];
            if (wallIdx >= 0 && prevCell.walls[wallIdx]) {
              hit = true;
            }
          }
        }
  
        const correctedDist = dist * Math.cos(rayAngle - baseAngle);
        const wallHeight = Math.min(height / correctedDist, height);
  
        // Brick red base color with shading
        const shade = Math.max(255 - correctedDist * 50, 40);
        const r = Math.floor(180 + 0.2 * shade); // dark red highlight
        const g = Math.floor(50 + 0.05 * shade);
        const b = Math.floor(50 + 0.05 * shade);
        ctx.fillStyle = `rgb(${r},${g},${b})`;
        ctx.fillRect(i, (height - wallHeight) / 2, 1, wallHeight);
  
        // Optional brick grout lines
        const brickHeight = 10;
        for (let y = 0; y < wallHeight; y += brickHeight) {
          ctx.fillStyle = 'rgba(0,0,0,0.2)';
          ctx.fillRect(i, (height - wallHeight) / 2 + y, 1, 1);
        }
      }
    }, [player, grid, cols, rows, width, height]);
  
    const cellX = Math.floor(player.x);
    const cellY = Math.floor(player.y);
    const atEnd = cellX === cols - 1 && cellY === rows - 1;

  const processDirection = useCallback(
    (direction) => {
      const [dx, dy] = VECTORS[facing];
      const { x, y } = playerPos;
      const cell = grid[y][x];
      const dirIdx = DIRECTIONS.indexOf(facing);

      switch (direction) {
        case 'ArrowLeft':
          rotate(true);
          setFacing(d => DIRECTIONS[(DIRECTIONS.indexOf(d) + 3) % 4]);
          break;
        case 'ArrowRight':
          rotate(false);
          setFacing(d => DIRECTIONS[(DIRECTIONS.indexOf(d) + 1) % 4]);
          break;
        case 'ArrowUp':
          move(true);
          if (!cell.walls[dirIdx]) setPlayerPos({ x: x + dx, y: y + dy });
          break;
        case 'ArrowDown':
          move(false);
          if (!cell.walls[(dirIdx + 2) % 4]) setPlayerPos({ x: x - dx, y: y - dy });
          break;
        default:
          break;
      }
    },
    [facing, playerPos, grid, setPlayerPos, move]
  );

  useEffect(() => {
     const handleKey = (e) => {
      if (["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(e.key)) {
        e.preventDefault();
        processDirection(e.key);
      }
    };

    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [processDirection]);

  const atStart = playerPos.x === 0 && playerPos.y === 0;

  return (
    <>
      <div className="mazeWalker" style={{ width }}>
        <p style={{ fontWeight: 'bold' }}>KEY</p>
        <p>⬅️ / ➡️ – Turn left/right</p>
        <p>⬆️ / ⬇️ – Move forward/backward if no wall blocks the way</p>
        <hr />
        <p>
          Facing: <strong>{DIRECTION_NAMES[facing]}</strong>; Location:{' '}
          <strong>({playerPos.x}, {playerPos.y})</strong>;
        </p>
        {atStart && <p>Currently at Start of Maze...</p>}
      </div>

      <div id="Arrows">
        <button onClick={() => processDirection('ArrowUp')}>
          <FaArrowCircleUp />
        </button>
        <button onClick={() => processDirection('ArrowDown')}>
          <FaArrowCircleDown />
        </button>
        <button onClick={() => processDirection('ArrowLeft')}>
          <FaArrowCircleLeft />
        </button>
        <button onClick={() => processDirection('ArrowRight')}>
          <FaArrowCircleRight />
        </button>
      </div>

      <div>
        <p style={{ fontWeight: 'bold' }}>First Person View</p>
        <canvas
          ref={canvasRef}
          width={width}
          height={height}
          style={{ display: 'block' }}
        />
        {atEnd && <p style={{ fontWeight: 'bold' }}>🎉 Congrats, you finished!</p>}
      </div>
    </>
  );
};

export default MazeWalker;
